import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { menuData as fallbackData } from '../data/menuData';

// Default menu ID - matches what was uploaded to Supabase
const DEFAULT_MENU_ID = 'after_eight';

export const useMenuData = (menuId = DEFAULT_MENU_ID) => {
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMenuData = async () => {
      // If supabase is not configured, use fallback data
      if (!supabase) {
        console.warn('Supabase not configured, using fallback data');
        setMenuData(fallbackData);
        setLoading(false);
        return;
      }

      try {
        // Fetch categories for the specified menu
        const { data: categories, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .eq('menu_id', menuId)
          .order('order_index', { ascending: true });

        if (categoriesError) throw categoriesError;

        // Fetch all items for these categories
        const categoryIds = categories.map(c => c.id);
        const { data: items, error: itemsError } = await supabase
          .from('items')
          .select('*')
          .in('category_id', categoryIds)
          .order('order_index', { ascending: true });

        if (itemsError) throw itemsError;

        // Transform the data into the expected format
        const transformedData = {};

        categories.forEach(category => {
          const categoryItems = items.filter(item => item.category_id === category.id);
          // Use category name as key (sanitized) for URL compatibility
          const categoryKey = category.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          transformedData[categoryKey] = {
            id: categoryKey,
            name: category.name,
            description: category.description || '',
            items: categoryItems.map(item => ({
              name: item.name,
              price: item.price,
              description: item.description || ''
            }))
          };
        });

        setMenuData(transformedData);
      } catch (err) {
        console.error('Error fetching menu data:', err);
        setError(err);
        // Fallback to static data if fetch fails
        setMenuData(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, [menuId]);

  return { menuData, loading, error };
};

export const useCategoryData = (categoryId, menuId = DEFAULT_MENU_ID) => {
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategoryData = async () => {
      // If supabase is not configured, use fallback data
      if (!supabase) {
        console.warn('Supabase not configured, using fallback data');
        if (fallbackData[categoryId]) {
          setCategory(fallbackData[categoryId]);
        }
        setLoading(false);
        return;
      }

      try {
        // Fetch all categories for this menu to find by sanitized name
        const { data: categories, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .eq('menu_id', menuId)
          .order('order_index', { ascending: true });

        if (categoriesError) throw categoriesError;

        // Find the category that matches the sanitized ID
        const categoryData = categories.find(cat => {
          const sanitizedName = cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
          return sanitizedName === categoryId;
        });

        if (!categoryData) {
          throw new Error(`Category not found: ${categoryId}`);
        }

        // Fetch items for this category
        const { data: items, error: itemsError } = await supabase
          .from('items')
          .select('*')
          .eq('category_id', categoryData.id)
          .order('order_index', { ascending: true });

        if (itemsError) throw itemsError;

        const categoryKey = categoryData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
        setCategory({
          id: categoryKey,
          name: categoryData.name,
          description: categoryData.description || '',
          items: items.map(item => ({
            name: item.name,
            price: item.price,
            description: item.description || ''
          }))
        });
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError(err);
        // Fallback to static data
        if (fallbackData[categoryId]) {
          setCategory(fallbackData[categoryId]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryData();
  }, [categoryId, menuId]);

  return { category, loading, error };
};
